import type { CheckResult } from '../../api/brandingApi';

interface Props {
  checks: CheckResult[];
}

const CATEGORY_LABEL: Record<string, string> = {
  HTTPS: 'HTTPS & Security',
  ROBOTS: 'Robots.txt',
  SITEMAP: 'Sitemap',
  CRAWLING: 'Crawlability',
  INDEXABILITY: 'Indexability',
  CANONICAL: 'Canonical',
  TITLE_META: 'Title & Meta',
  HEADINGS: 'Headings',
  SCHEMA: 'Schema Markup',
  STRUCTURED_DATA: 'Structured Data',
  SOCIAL: 'Open Graph / Social',
  GSC_OWNER: 'GSC Verification Signals',
  PERFORMANCE_BASIC: 'Performance',
  PERFORMANCE: 'Performance',
  DNS_INFRA: 'DNS & Infrastructure',
  PAGE_TECHNICAL: 'Page Technical',
  ON_PAGE_SEO: 'On-Page SEO',
  OWNERSHIP: 'Verification Signals',
  AI_CRAWLING: 'AI Crawler Access',
  AI_READINESS: 'AI-Readable Structure',
  LLM_DISCOVERY: 'LLM Discovery Files',
  ENTITY_SIGNALS: 'Entity & Trust Signals',
  AI_CITATION: 'AI Citation Readiness',
  SYSTEM: 'System',
};

const CHECK_LABELS: Record<string, string> = {
  robots_txt_found: 'Robots.txt Found',
  robots_txt_crawl_blocking: 'Full-Site Crawl Blocking',
  robots_txt_syntax: 'Robots.txt Syntax',
  robots_txt_important_dirs: 'Sensitive Directory Rules',
  robots_txt_sitemap_directive: 'Sitemap Directive',
  robots_txt_search_engines: 'Search Engine and AI Crawler Access',
  robots_txt_wordpress: 'WordPress Robots Configuration',
  robots_txt_conflicts: 'Allow and Disallow Conflicts',
  sitemap_found: 'Sitemap Found',
  sitemap_xml_valid: 'Sitemap XML Validity',
  https_availability: 'HTTPS Availability',
  hsts_header: 'HSTS Header',
  http_to_https_redirect: 'HTTP to HTTPS Redirect',
  dns_resolution: 'DNS Resolution',
  homepage_status: 'Homepage Availability',
  www_nonwww_consistency: 'Preferred Domain Consistency',
  compression: 'Response Compression',
  caching_headers: 'Browser Caching Headers',
  schema_markup: 'Schema Markup',
  open_graph: 'Open Graph Tags',
  twitter_card: 'Twitter Card',
  social_profile_links: 'Social Profile Links',
  gsc_meta_tag: 'GSC HTML Verification Signal',
  gsc_dns_verification: 'GSC DNS Verification Signal',
};

const STATUS_LABEL: Record<string, string> = {
  PASS: 'Pass',
  WARNING: 'Warning',
  FAIL: 'Issue',
  INFO: 'Info',
};

const STATUS_BADGE: Record<string, string> = {
  PASS: 'asneris-badge asneris-badge-success',
  WARNING: 'asneris-badge asneris-badge-warning',
  FAIL: 'asneris-badge asneris-badge-error',
  INFO: 'asneris-badge asneris-badge-info',
};

const SEVERITY_BADGE: Record<string, string> = {
  HIGH: 'asneris-badge asneris-badge-error',
  MEDIUM: 'asneris-badge asneris-badge-warning',
  LOW: 'asneris-badge asneris-badge-info',
  INFO: 'asneris-badge',
};

function displayName(name: string): string {
  return (
    CHECK_LABELS[name] ??
    name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase())
  );
}

function groupByCategory(checks: CheckResult[]): Record<string, CheckResult[]> {
  return checks.reduce<Record<string, CheckResult[]>>((groups, check) => {
    const category = check.category ?? 'OTHER';
    (groups[category] ||= []).push(check);
    return groups;
  }, {});
}

function metrics(checks: CheckResult[]) {
  const passed = checks.filter((check) => check.status === 'PASS').length;
  const warnings = checks.filter((check) => check.status === 'WARNING').length;
  const failed = checks.filter((check) => check.status === 'FAIL').length;
  const score = checks.length
    ? Math.round(checks.reduce((sum, check) => sum + (check.score ?? 0), 0) / checks.length)
    : 0;

  return { passed, warnings, failed, score };
}

function statusColor(failed: number, warnings: number): string {
  if (failed > 0) return '#dc2626';
  if (warnings > 0) return '#d97706';
  return '#15803d';
}

export default function CheckCategoryAccordion({ checks }: Props) {
  const groups = groupByCategory(checks);

  return (
    <>
      <style>{`
        .asneris-detail-group summary::-webkit-details-marker { display: none; }
        .asneris-detail-group[open] .asneris-detail-chevron { transform: rotate(180deg); }
        .asneris-detail-table-wrap { overflow-x: auto; }
        .asneris-detail-table {
          width: 100%;
          min-width: 780px;
          border-collapse: separate;
          border-spacing: 0;
          font-size: 12px;
        }
        .asneris-detail-table th {
          position: sticky;
          top: 0;
          z-index: 1;
          background: #f8fbfe;
          color: var(--asneris-muted);
          font-size: 10px;
          font-weight: 800;
          letter-spacing: .03em;
          text-align: left;
          text-transform: uppercase;
          border-bottom: 1px solid var(--asneris-border);
          padding: 10px 10px;
          white-space: nowrap;
        }
        .asneris-detail-table td {
          vertical-align: top;
          border-bottom: 1px solid var(--asneris-border);
          padding: 12px 10px;
          color: var(--asneris-text);
        }
        .asneris-detail-table tbody tr:last-child td { border-bottom: none; }
        .asneris-detail-table tbody tr:hover td { background: #fbfdff; }
        .asneris-detail-table-check { width: 18%; min-width: 145px; }
        .asneris-detail-table-status { width: 86px; }
        .asneris-detail-table-severity { width: 92px; }
        .asneris-detail-table-score { width: 70px; text-align: right; white-space: nowrap; }
        .asneris-detail-table-detail { width: 30%; min-width: 210px; }
        .asneris-detail-table-recommendation { width: 30%; min-width: 230px; }
      `}</style>

      <div style={{ display: 'grid', gap: '8px' }}>
        {Object.entries(groups).map(([category, items]) => {
          const data = metrics(items);
          const color = statusColor(data.failed, data.warnings);
          const shouldOpen = data.failed > 0 || data.warnings > 0;

          return (
            <details
              key={category}
              open={shouldOpen}
              className="asneris-detail-group"
              style={{
                border: '1px solid var(--asneris-border)',
                borderRadius: '10px',
                overflow: 'hidden',
                background: '#fff',
              }}
            >
              <summary
                style={{
                  listStyle: 'none',
                  cursor: 'pointer',
                  display: 'grid',
                  gridTemplateColumns: 'minmax(180px, 1fr) auto auto auto auto',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '12px 14px',
                  userSelect: 'none',
                }}
              >
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--asneris-navy)' }}>
                    {CATEGORY_LABEL[category] ?? category}
                  </div>
                  <div style={{ marginTop: '2px', fontSize: '10px', color: 'var(--asneris-muted)' }}>
                    {items.length} check{items.length === 1 ? '' : 's'}
                  </div>
                </div>

                <span className="asneris-badge asneris-badge-success">
                  {data.passed} passed
                </span>
                <span className="asneris-badge asneris-badge-warning">
                  {data.warnings} warning{data.warnings === 1 ? '' : 's'}
                </span>
                <span className="asneris-badge asneris-badge-error">
                  {data.failed} issue{data.failed === 1 ? '' : 's'}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <strong style={{ color, fontSize: '14px' }}>{data.score}/100</strong>
                  <svg
                    className="asneris-detail-chevron"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                    style={{ transition: 'transform .2s ease' }}
                  >
                    <polyline
                      points="6,9 12,15 18,9"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </summary>

              <div className="asneris-detail-table-wrap" style={{ borderTop: '1px solid var(--asneris-border)' }}>
                <table className="asneris-detail-table">
                  <thead>
                    <tr>
                      <th className="asneris-detail-table-check">Check</th>
                      <th className="asneris-detail-table-status">Status</th>
                      <th className="asneris-detail-table-severity">Severity</th>
                      <th className="asneris-detail-table-score">Score</th>
                      <th className="asneris-detail-table-detail">Detail</th>
                      <th className="asneris-detail-table-recommendation">Recommendation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((check) => {
                      const hasRecommendation =
                        (check.status === 'FAIL' || check.status === 'WARNING') && Boolean(check.recommendation);
                      const statusClassName = STATUS_BADGE[check.status] ?? 'asneris-badge';
                      const severityClassName = SEVERITY_BADGE[check.severity] ?? 'asneris-badge';

                      return (
                        <tr key={`${check.category}-${check.checkName}`}>
                          <td className="asneris-detail-table-check">
                            <strong style={{ color: 'var(--asneris-navy)', fontSize: '12px' }}>
                              {displayName(check.checkName)}
                            </strong>
                          </td>
                          <td className="asneris-detail-table-status">
                            <span className={statusClassName} style={{ fontSize: '10px', padding: '2px 8px' }}>
                              {STATUS_LABEL[check.status] ?? check.status}
                            </span>
                          </td>
                          <td className="asneris-detail-table-severity">
                            <span className={severityClassName} style={{ fontSize: '10px', padding: '2px 8px' }}>
                              {check.severity}
                            </span>
                          </td>
                          <td className="asneris-detail-table-score">
                            <strong style={{ color: statusColor(check.status === 'FAIL' ? 1 : 0, check.status === 'WARNING' ? 1 : 0) }}>
                              {check.score ?? 0}/100
                            </strong>
                          </td>
                          <td className="asneris-detail-table-detail">
                            <p style={{ margin: 0, lineHeight: 1.55 }}>{check.message}</p>
                          </td>
                          <td className="asneris-detail-table-recommendation">
                            {hasRecommendation ? (
                              <p
                                style={{
                                  margin: 0,
                                  lineHeight: 1.55,
                                  color: check.status === 'FAIL' ? '#991b1b' : '#92400e',
                                  fontWeight: 600,
                                }}
                              >
                                {check.recommendation}
                              </p>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </details>
          );
        })}
      </div>
    </>
  );
}
